/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * CDU-consent-aware cookie wrapper. When consent is accepted, reads/writes real
 * browser cookies; otherwise forwards to in-memory storage (RAM only).
 */

import { getInMemoryCookieStorage, hasConsent } from './cookie-in-memory';

export {
  migrateInMemoryToRealCookies,
  clearInMemoryCookieStorage,
  getInMemoryCookieStorageContents,
} from './cookie-in-memory';

interface CookieSetOptions {
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Cookie wrapper interface that forwards to in-memory storage when consent is not accepted.
 */
export interface CookieWrapper {
  set(name: string, value: string, options?: CookieSetOptions): void;
  get(name: string): string | null;
  delete(name: string, options?: { path?: string; domain?: string }): void;
  getAll(): string;
}

/**
 * Ordered attribute serializers mapping each optional cookie attribute to the
 * `document.cookie` fragment it produces when present.
 */
const COOKIE_ATTRIBUTE_BUILDERS: Array<(options: CookieSetOptions) => string | null> = [
  (options) => (options.expires ? `; expires=${options.expires.toUTCString()}` : null),
  (options) => (options.path ? `; path=${options.path}` : null),
  (options) => (options.domain ? `; domain=${options.domain}` : null),
  (options) => (options.secure ? '; secure' : null),
  (options) => (options.sameSite ? `; SameSite=${options.sameSite}` : null),
];

/**
 * Serializes a cookie name/value plus options into a `document.cookie`-ready
 * string, URL-encoding the name and value.
 *
 * @param name Cookie name.
 * @param value Cookie value.
 * @param options Optional expiry/path/domain/secure/sameSite attributes.
 * @returns The assembled cookie string.
 */
function buildCookieString(name: string, value: string, options?: CookieSetOptions): string {
  const base = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  const attributes = COOKIE_ATTRIBUTE_BUILDERS.map((build) => build(options ?? {})).filter(
    (fragment): fragment is string => fragment !== null
  );
  return base + attributes.join('');
}

/**
 * Sets a cookie in real browser storage when consent is accepted, otherwise in
 * the in-memory fallback.
 *
 * @param name Cookie name.
 * @param value Cookie value.
 * @param options Optional cookie attributes (only `expires` used in-memory).
 */
function setCookie(name: string, value: string, options?: CookieSetOptions): void {
  if (hasConsent()) {
    document.cookie = buildCookieString(name, value, options);
    return;
  }
  getInMemoryCookieStorage().set(name, value, { expires: options?.expires });
}

/**
 * Reads a cookie from real browser storage when consent is accepted, otherwise
 * from the in-memory fallback.
 *
 * @param name Cookie name to read.
 * @returns The decoded cookie value, or `null` when absent.
 */
function getCookie(name: string): string | null {
  if (hasConsent()) {
    const cookies = document.cookie.split(';');
    const cookie = cookies.find((c) => c.trim().startsWith(`${encodeURIComponent(name)}=`));
    if (!cookie) return null;
    return decodeURIComponent(cookie.split('=')[1]);
  }
  return getInMemoryCookieStorage().get(name);
}

/**
 * Deletes a cookie from real browser storage when consent is accepted (by
 * expiring it), otherwise from the in-memory fallback.
 *
 * @param name Cookie name to delete.
 * @param options Optional path/domain scoping for the real-cookie deletion.
 */
function deleteCookie(name: string, options?: { path?: string; domain?: string }): void {
  if (hasConsent()) {
    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    if (options?.path) cookieString += `; path=${options.path}`;
    if (options?.domain) cookieString += `; domain=${options.domain}`;
    document.cookie = cookieString;
    return;
  }
  getInMemoryCookieStorage().delete(name);
}

/**
 * Returns all cookies as a `; `-joined string, from real browser storage when
 * consent is accepted, otherwise from the in-memory fallback.
 *
 * @returns The serialized cookie list.
 */
function getAllCookies(): string {
  if (hasConsent()) return document.cookie;
  return getInMemoryCookieStorage().getAll().join('; ');
}

/**
 * Creates a cookie wrapper that enforces CDU consent. When consent is ACCEPTED it
 * uses real browser cookies; when PENDING or REJECTED it uses in-memory storage
 * (RAM only, lost on reload/close) as a temporary, non-persistent fallback.
 */
export function createCookieWrapper(): CookieWrapper {
  return {
    set: setCookie,
    get: getCookie,
    delete: deleteCookie,
    getAll: getAllCookies,
  };
}

let cookieWrapperInstance: CookieWrapper | null = null;

/**
 * Returns the process-wide {@link CookieWrapper}, creating it on first use.
 *
 * @returns The singleton cookie wrapper instance.
 */
export function getCookieWrapper(): CookieWrapper {
  if (!cookieWrapperInstance) {
    cookieWrapperInstance = createCookieWrapper();
  }
  return cookieWrapperInstance;
}
